/*
 * Leave helpers for the Sales Team page. Leave dates are IST calendar days,
 * inclusive on both ends, compared as YYYY-MM-DD strings.
 */

import moment from 'moment-timezone';
import type { NewLeave, TeamLeave } from './api';

const IST_TZ = 'Asia/Kolkata';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const ist_today = (now: Date = new Date()): string =>
  moment(now).tz(IST_TZ).format('YYYY-MM-DD');

// Current and future leaves only, soonest first.
export const visible_leaves = (
  leaves: TeamLeave[] | null | undefined,
  today: string
): TeamLeave[] =>
  (leaves || [])
    .filter((l) => l && l.to_date >= today)
    .sort((a, b) => a.from_date.localeCompare(b.from_date) || a.to_date.localeCompare(b.to_date));

const overlaps = (a: { from_date: string; to_date: string }, b: TeamLeave): boolean =>
  a.from_date <= b.to_date && b.from_date <= a.to_date;

export const format_range = (from: string, to: string): string => {
  const f = moment(from, 'YYYY-MM-DD', true);
  const t = moment(to, 'YYYY-MM-DD', true);
  if (!f.isValid() || !t.isValid()) return [from, to].filter(Boolean).join(' – ');
  if (from === to) return f.format('D MMM YYYY');
  if (f.year() !== t.year()) return `${f.format('D MMM YYYY')} – ${t.format('D MMM YYYY')}`;
  if (f.month() !== t.month()) return `${f.format('D MMM')} – ${t.format('D MMM YYYY')}`;
  return `${f.format('D')} – ${t.format('D MMM YYYY')}`;
};

// Returns the first problem, or null when the leave can be submitted.
export const validate_new_leave = (
  leave: Partial<NewLeave> | null | undefined,
  existing: TeamLeave[] | null | undefined,
  today: string
): string | null => {
  const from_date = leave?.from_date || '';
  const to_date = leave?.to_date || '';
  if (!DATE_RE.test(from_date) || !DATE_RE.test(to_date)) return 'Pick a start and end date';
  if (from_date > to_date) return 'Start date must be on or before the end date';
  if (to_date < today) return 'This leave is entirely in the past';
  const clash = (existing || []).find((l) => overlaps({ from_date, to_date }, l));
  if (clash) return `Overlaps the leave on ${format_range(clash.from_date, clash.to_date)}`;
  return null;
};
